import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { MagnifyingGlass, FolderDashed } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { formatCompact, formatPct } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { NewProjectDialog } from "@/components/NewProjectDialog";
import { ProjectCard } from "@/components/ProjectCard";
import { Skeleton } from "@/components/ui/skeleton";

function PortfolioStrip({ projects }) {
  const active = projects.filter((p) => p.status !== "archived");
  const sum = (k) => active.reduce((s, p) => s + p.totals[k], 0);
  const rev = sum("revenue");
  const profit = sum("profit");
  const stats = [
    { label: "Active Projects", value: active.length, id: "stat-active-projects" },
    { label: "Portfolio Govt BOQ", value: formatCompact(sum("govt_amount")), id: "stat-portfolio-boq" },
    { label: "Expected Profit", value: formatCompact(profit), id: "stat-portfolio-profit", tone: profit >= 0 ? "text-emerald-600" : "text-rose-600" },
    { label: "Blended Margin", value: formatPct(rev ? (profit / rev) * 100 : 0), id: "stat-portfolio-margin" },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 border border-slate-200 bg-white rounded-sm divide-x divide-slate-200 [&>*:nth-child(3)]:border-l-0 lg:[&>*:nth-child(3)]:border-l [&>*:nth-child(n+3)]:border-t lg:[&>*:nth-child(n+3)]:border-t-0">
      {stats.map((s) => (
        <div key={s.label} className="p-5" data-testid={s.id}>
          <div className="text-xs uppercase tracking-wide text-slate-500 font-medium">{s.label}</div>
          <div className={`mt-2 font-heading text-2xl lg:text-3xl font-bold tabular ${s.tone || "text-slate-900"}`}>{s.value}</div>
        </div>
      ))}
    </div>
  );
}

export default function Projects() {
  const { user } = useAuth();
  const [projects, setProjects] = useState(null);
  const [q, setQ] = useState("");
  const [view, setView] = useState("active");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/projects");
      setProjects(data);
    } catch (e) {
      toast.error(errMsg(e, "Could not load projects"));
      setProjects([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const shown = useMemo(() => (projects || []).filter((p) =>
    (view === "archived" ? p.status === "archived" : p.status !== "archived") &&
    `${p.name} ${p.client} ${p.location}`.toLowerCase().includes(q.toLowerCase())), [projects, q, view]);

  return (
    <div className="max-w-[1600px] p-4 sm:p-6 lg:p-8 space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 rise-in">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold">Welcome, {user?.name?.split(" ")[0]}</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-1">BOQ Projects</h1>
        </div>
        <NewProjectDialog />
      </div>

      {projects && <PortfolioStrip projects={projects} />}

      <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
        <div className="inline-flex border border-slate-200 rounded-sm bg-white p-0.5">
          {["active", "archived"].map((v) => (
            <button key={v} data-testid={`filter-${v}`} onClick={() => setView(v)}
              className={`px-4 py-1.5 text-sm font-medium rounded-sm capitalize transition-colors ${view === v ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              {v}
            </button>
          ))}
        </div>
        <div className="relative sm:w-80">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <input data-testid="project-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search projects, clients, locations"
            className="w-full h-10 pl-9 pr-3 text-sm bg-white border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500" />
        </div>
      </div>

      {!projects ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-56 rounded-sm" />)}</div>
      ) : shown.length === 0 ? (
        <div data-testid="projects-empty-state" className="border-2 border-dashed border-slate-300 rounded-sm bg-white p-12 text-center">
          <FolderDashed className="mx-auto text-slate-400" size={44} weight="duotone" />
          <h3 className="font-heading text-xl font-bold text-slate-800 mt-4">{view === "archived" ? "No archived projects" : "No projects yet"}</h3>
          <p className="text-sm text-slate-500 mt-1">Create a project and upload your Government BOQ to start estimating.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="projects-grid">
          {shown.map((p, i) => <ProjectCard key={p.project_id} project={p} index={i} onChanged={load} />)}
        </div>
      )}
    </div>
  );
}
